/**
 * Utilitário para obter o Tenant ID para requisições multi-tenant.
 *
 * Em desenvolvimento: usa a variável de ambiente VITE_TENANT_ID
 * Em produção: não envia header (backend detecta pelo subdomínio)
 *
 * IMPORTANTE: Em produção, o mesmo build roda em múltiplos subdomínios:
 * - corelgo.one1tec.com.br → one1print
 * - corel.one1tec.com.br → one1printdf
 * - corel-uniforme.one1tec.com.br → oneuniformes
 *
 * @see docs/14-12/CONFIGURACAO_FRONTEND.md
 */

/**
 * Mapeamento de subdomínios de produção para tenant IDs.
 *
 * ⚠️ ATENÇÃO: Este mapeamento é usado quando o mesmo build roda em múltiplos subdomínios.
 * Se adicionar novos subdomínios, atualize este mapeamento.
 */
const SUBDOMAIN_TO_TENANT_MAP: Record<string, string> = {
	corelgo: "one1print",
	corel: "one1printdf",
	"corel-uniforme": "oneuniformes",
};

/**
 * Retorna a regiao da aplicacao a partir da variavel de ambiente VITE_APP_REGION.
 * Anteriormente era um mapa hardcoded tenant->regiao; agora cada deploy configura via .env.
 */

/**
 * Retorna o Tenant ID para enviar no header X-Tenant-Id.
 *
 * @returns O tenant ID se em desenvolvimento e configurado, null caso contrário
 *
 * @example
 * const tenantId = getTenantId();
 * if (tenantId) {
 *   headers['X-Tenant-Id'] = tenantId;
 * }
 */
export function getTenantId(): string | null {

	// Em desenvolvimento, usar variável de ambiente se definida
	const tenantId = import.meta.env.VITE_TENANT_ID;

	if (tenantId && typeof tenantId === "string" && tenantId.trim() !== "") {
		return tenantId.trim();
	}

	// Se não definida, não enviar - backend usa fallback 'dev'
	return null;
}

/**
 * Retorna o valor do header x-app-region a partir de VITE_APP_REGION.
 */
export function getAppRegion(): string | null {
	const region = import.meta.env.VITE_APP_REGION;
	if (region && typeof region === "string" && region.trim() !== "") {
		return region.trim();
	}
	return null;
}

/**
 * Adiciona o header X-Tenant-Id aos headers existentes se necessário.
 *
 * @param headers - Headers existentes (Record ou Headers)
 * @returns Headers com X-Tenant-Id adicionado se aplicável
 */
export function addTenantHeader(
	headers: Record<string, string> = {},
): Record<string, string> {
	const tenantId = getTenantId();

	if (tenantId) {
		return {
			...headers,
			"X-Tenant-Id": tenantId,
		};
	}

	return headers;
}

/**
 * Adiciona o header x-app-region aos headers existentes quando houver mapeamento.
 */
export function addAppRegionHeader(
	headers: Record<string, string> = {},
): Record<string, string> {
	const appRegion = getAppRegion();

	if (appRegion) {
		return {
			...headers,
			"x-app-region": appRegion,
		};
	}

	return headers;
}

/**
 * Retorna o Tenant ID para WebSocket (query string ou header).
 *
 * Diferente de getTenantId(), esta função SEMPRE retorna um tenantId:
 * - Em desenvolvimento: usa VITE_TENANT_ID ou fallback 'dev'
 * - Em produção: extrai do hostname (subdomínio) ou fallback 'dev'
 *
 * @returns O tenant ID sempre (nunca null)
 *
 * @example
 * const tenantId = getTenantIdForWebSocket();
 * socket = io(url, { query: { tenantId } });
 */
export function getTenantIdForWebSocket(): string {
	// Em desenvolvimento, usar variável de ambiente se definida
	if (import.meta.env.DEV) {
		const tenantId = import.meta.env.VITE_TENANT_ID;


		if (tenantId && typeof tenantId === "string" && tenantId.trim() !== "") {
			return tenantId.trim();
		}

		// Fallback para 'dev' em desenvolvimento
		return "dev";
	}

	// Em produção, detectar pelo subdomínio usando mapeamento
	if (typeof window !== "undefined" && window.location) {
		const hostname = window.location.hostname;
		const subdomain = hostname.split(".")[0];

		// Verificar se o subdomínio está no mapeamento
		if (SUBDOMAIN_TO_TENANT_MAP[subdomain]) {
			return SUBDOMAIN_TO_TENANT_MAP[subdomain];
		}
	}

	// Fallback final
	return "dev";
}
